import { useMemo, useState } from 'react';
import { SERVICES_COMPTABLES } from '@artosera/shared';
import { creerVersionGrille, designerReste, modifierVersionGrille } from '../../lib/api.js';
import type { ComptabiliteDirection, TypeRecetteId, VersionGrille } from '../../types.js';
import { useMessageErreur } from '../Finances/commun.js';
import { libellesDe, moisPlus, ordreBeneficiaires } from './donnees.js';
import {
  appliquerApercu,
  corpsDesParts,
  memesParts,
  partsCompletes,
  partsEnVigueur,
  plafondDePart,
  resteParDefaut,
  type Modifs,
  type Parts,
} from './repartition.js';
import { useRelireComptabilite } from './useComptabilite.js';

/** Combien de mois de départ la grille propose, à partir du premier mois provisoire. */
const NB_DEPARTS = 6;

export interface PartsAffichees {
  /** Le service a-t-il des parts (une version en vigueur, ou un brouillon) ? */
  definies: boolean;
  /** Les parts de chaque bénéficiaire, le reste compris. Vide quand `definies` est faux. */
  valeurs: Parts;
}

export interface Brouillon {
  /** Les bénéficiaires, dans l'ordre du partage. */
  ordre: string[];
  libelles: Map<string, string>;
  /** Celui qui prend le reste ; `null` tant que le partage est vide. */
  resteId: string | null;
  /**
   * Le reste est-il désigné côté serveur (`recoitLeReste`) ? Sinon, l'écran en
   * propose un par défaut, mais les galeries exclues ne se répartissent pas.
   */
  resteDesigne: boolean;
  /** Désigne le bénéficiaire du reste : l'écriture part aussitôt vers l'API. */
  choisirReste: (id: string) => void;
  /** Les bénéficiaires qui ont chacun leur curseur : tous sauf celui du reste. */
  reglables: string[];
  depuis: string;
  departs: string[];
  choisirDepuis: (mois: string) => void;
  parts: (service: TypeRecetteId) => PartsAffichees;
  modifie: (service: TypeRecetteId) => boolean;
  servicesModifies: TypeRecetteId[];
  /** Vrai quand `points` a dû être ramené sous le plafond de 100 %. */
  changer: (service: TypeRecetteId, beneficiaireId: string, points: number) => boolean;
  annuler: () => void;
  enregistrer: () => Promise<void>;
  enCours: boolean;
  erreur: string | null;
  /** Le départ de la dernière version enregistrée, tant qu'on n'a rien retouché depuis. */
  enregistree: { depuis: string; services: number } | null;
  /** La comptabilité avec le brouillon appliqué, ou `undefined` quand rien n'est modifié. */
  apercu: ComptabiliteDirection | undefined;
}

/**
 * Le brouillon de la grille (ARTO-58). Il ne garde que ce que la direction a
 * touché : pour un service intact, les parts sont celles de la version en
 * vigueur. Le bénéficiaire du « reste » n'a pas de curseur : sa part est ce que
 * les autres laissent, donc le total ne dépasse jamais 100 %.
 *
 * Le « reste » est UN choix pour tout l'écran (les colonnes ne changent pas
 * d'un service à l'autre) : celui que le serveur désigne (`recoitLeReste`), qui
 * reçoit aussi les parts que les galeries exclues transfèrent. Le choisir ici
 * l'écrit aussitôt. Tant qu'aucun n'est désigné : le bénéficiaire qui reçoit
 * le plus des versions en vigueur, pour l'écran seulement.
 */
export function useBrouillon({
  donnees,
  versions,
  beneficiaires,
  types = SERVICES_COMPTABLES,
}: {
  donnees: ComptabiliteDirection | undefined;
  versions: readonly VersionGrille[];
  beneficiaires: ReadonlyArray<{ id: string; libelle: string; recoitLeReste?: boolean }>;
  /** Les types de recette que la grille règle, dans l'ordre de ses lignes. */
  types?: readonly TypeRecetteId[];
}): Brouillon {
  const message = useMessageErreur('comptabilite');
  const relire = useRelireComptabilite();
  const [departChoisi, setDepartChoisi] = useState<string | null>(null);
  const [resteChoisi, setResteChoisi] = useState<string | null>(null);
  const [modifs, setModifs] = useState<Modifs>({});
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enregistree, setEnregistree] = useState<Brouillon['enregistree']>(null);

  const ordre = useMemo(
    () =>
      donnees
        ? ordreBeneficiaires(
            donnees,
            beneficiaires.map((b) => b.id),
          )
        : beneficiaires.map((b) => b.id),
    [donnees, beneficiaires],
  );
  const libelles = useMemo(() => {
    const table = donnees ? libellesDe(donnees) : new Map<string, string>();
    for (const b of beneficiaires) table.set(b.id, b.libelle);
    return table;
  }, [donnees, beneficiaires]);

  const premierProvisoire = donnees?.mois.find((m) => m.statut !== 'cloture')?.mois ?? donnees?.a;
  const departs = useMemo(
    () =>
      premierProvisoire
        ? Array.from({ length: NB_DEPARTS }, (_, i) => moisPlus(premierProvisoire, i))
        : [],
    [premierProvisoire],
  );
  const depuis =
    departChoisi !== null && departs.includes(departChoisi) ? departChoisi : (departs[0] ?? '');

  // Le reste du serveur ; le choix local ne vit que le temps de son écriture.
  const resteServeur = beneficiaires.find((b) => b.recoitLeReste === true)?.id ?? null;
  const resteId =
    resteChoisi !== null && ordre.includes(resteChoisi)
      ? resteChoisi
      : resteServeur !== null && ordre.includes(resteServeur)
        ? resteServeur
        : (resteParDefaut(versions, depuis, ordre) ?? ordre[0] ?? null);
  const reglables = ordre.filter((id) => id !== resteId);

  const base = (service: TypeRecetteId): Parts | null =>
    depuis === '' ? null : partsEnVigueur(versions, service, depuis);

  /** Les parts hors reste d'un service, telles qu'on les édite. */
  const courantes = (service: TypeRecetteId): Parts => {
    const modifiees = modifs[service];
    if (modifiees) return modifiees;
    const parts: Parts = {};
    const depart = base(service);
    for (const id of reglables) parts[id] = depart?.[id] ?? 0;
    return parts;
  };

  const completes = (service: TypeRecetteId): Parts | null => {
    if (resteId === null) return null;
    const modifiees = modifs[service];
    if (modifiees) return partsCompletes(modifiees, resteId, ordre);
    const depart = base(service);
    return depart === null ? null : partsCompletes(depart, resteId, ordre);
  };

  const parts = (service: TypeRecetteId): PartsAffichees => {
    const valeurs = completes(service);
    return valeurs === null ? { definies: false, valeurs: {} } : { definies: true, valeurs };
  };

  const servicesModifies = types.filter((service) => modifs[service] !== undefined);

  const changer = (service: TypeRecetteId, id: string, points: number): boolean => {
    if (resteId === null) return false;
    const actuelles = courantes(service);
    const plafond = plafondDePart(actuelles, id, resteId);
    const valeur = Math.max(0, Math.min(Math.round(points), plafond));
    const suivantes: Parts = { ...actuelles, [id]: valeur };
    const depart = base(service);
    setEnregistree(null);
    setErreur(null);
    setModifs((precedentes) => {
      const reste = { ...precedentes };
      // Revenir aux parts de la version en vigueur : plus rien à enregistrer.
      if (depart !== null && memesParts(partsCompletes(suivantes, resteId, ordre), depart)) {
        delete reste[service];
      } else {
        reste[service] = suivantes;
      }
      return reste;
    });
    return valeur < Math.round(points);
  };

  /** Les services touchés gardent leur sens : parts complètes, moins le nouveau reste. */
  const rebaser = (ancien: string, nouveau: string): void => {
    setModifs((precedentes) => {
      const suivantes: Modifs = {};
      for (const [service, modifiees] of Object.entries(precedentes)) {
        if (!modifiees) continue;
        const pleines = partsCompletes(modifiees, ancien, ordre);
        const { [nouveau]: _reste, ...autres } = pleines;
        suivantes[service] = autres;
      }
      return suivantes;
    });
  };

  const choisirReste = (id: string): void => {
    if (resteId === null || id === resteId) return;
    const ancien = resteId;
    rebaser(ancien, id);
    setResteChoisi(id);
    setErreur(null);
    void (async () => {
      try {
        await designerReste(id);
        await relire();
        // Le serveur le dit désormais : le choix local s'efface derrière lui.
        setResteChoisi(null);
      } catch (echec) {
        // Refusé (un autre le garde, ou il exclut une galerie) : on revient.
        rebaser(id, ancien);
        setResteChoisi(ancien === resteServeur ? null : ancien);
        setErreur(message(echec));
        await relire();
      }
    })();
  };

  const annuler = (): void => {
    setModifs({});
    setErreur(null);
    setEnregistree(null);
  };

  const enregistrer = async (): Promise<void> => {
    if (resteId === null || servicesModifies.length === 0) return;
    setEnCours(true);
    setErreur(null);
    let ecrits = 0;
    try {
      for (const service of servicesModifies) {
        const pleines = completes(service);
        if (pleines === null) continue;
        const corps = corpsDesParts(pleines, ordre);
        const existante = versions.find((v) => v.service === service && v.depuisMois === depuis);
        if (existante) await modifierVersionGrille(existante.id, { parts: corps });
        else await creerVersionGrille({ service, depuisMois: depuis, parts: corps });
        ecrits += 1;
        // Ce qui est écrit n'est plus un brouillon, même si la suite échoue.
        setModifs((precedentes) => {
          const reste = { ...precedentes };
          delete reste[service];
          return reste;
        });
      }
      setEnregistree({ depuis, services: ecrits });
    } catch (echec) {
      setErreur(message(echec));
    } finally {
      setEnCours(false);
      await relire();
    }
  };

  const apercu = useMemo(() => {
    if (!donnees || resteId === null) return undefined;
    const entree: Partial<Record<TypeRecetteId, Parts>> = {};
    for (const [service, modifiees] of Object.entries(modifs)) {
      if (modifiees) entree[service] = partsCompletes(modifiees, resteId, ordre);
    }
    if (Object.keys(entree).length === 0) return undefined;
    return appliquerApercu(donnees, { depuis, parts: entree, versions, ordre, libelles });
  }, [donnees, modifs, resteId, depuis, versions, ordre, libelles]);

  return {
    ordre,
    libelles,
    resteId,
    resteDesigne: resteServeur !== null,
    choisirReste,
    reglables,
    depuis,
    departs,
    choisirDepuis: (mois) => {
      setDepartChoisi(mois);
      setEnregistree(null);
    },
    parts,
    modifie: (service) => modifs[service] !== undefined,
    servicesModifies,
    changer,
    annuler,
    enregistrer,
    enCours,
    erreur,
    enregistree,
    apercu,
  };
}
